import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FlaskConical } from "lucide-react";
import { DEV_PERSONAS, useAuth } from "../auth/AuthContext";

// The Development persona switcher, marked exactly as Summit Engineering marks its own stub sign-in. It is the only
// sign-in Summit Operations has until the Entra app registration exists (phase P1, blocked on Q-O8); the server owns
// the persona → role mapping and refuses this scheme outside Development.

export function PersonaSwitcher() {
  const { persona, signInAsPersona, mode } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (mode !== "dev-persona") return null;
  const current = DEV_PERSONAS.find((p) => p.key === persona);

  return (
    <div className="relative flex-none">
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="persona-switcher"
        onClick={() => setOpen((v) => !v)}
        title="Development persona sign-in. Microsoft Entra ID replaces this once the Summit Operations app registration exists (Q-O8)."
        className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100"
      >
        <FlaskConical aria-hidden="true" className="h-3 w-3" />
        <span className="hidden sm:inline">Development sign-in</span>
        <span className="font-semibold">{current?.label ?? "Choose"}</span>
        <ChevronDown aria-hidden="true" className={`h-3 w-3 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Development personas"
          className="absolute right-0 top-full z-40 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          <p className="px-3 py-2 text-[11px] leading-snug text-slate-500">
            Development sign-in only. The server decides the roles for each persona; Microsoft Entra ID replaces this in phase P1.
          </p>
          {DEV_PERSONAS.map((p) => (
            <button
              key={p.key}
              type="button"
              role="menuitem"
              data-testid={`persona-${p.key}`}
              onClick={async () => {
                setOpen(false);
                const ok = await signInAsPersona(p.key);
                if (ok) navigate("/", { replace: true });
              }}
              className={`flex w-full flex-col items-start gap-0.5 rounded-md px-3 py-2 text-left hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                p.key === persona ? "bg-brand-50" : ""
              }`}
            >
              <span className="text-sm font-medium text-slate-800">{p.label}</span>
              <span className="text-[11px] leading-snug text-slate-500">{p.capability}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
